/**
 * City Agency Pages — Wave 7
 * Bratislava, Budapest, Zagreb, Riga, Vilnius, Cape Town, Kampala, Maputo, Algiers, Colombo
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools with modern stack.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, and managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance consulting.' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools with PCI-DSS and SOC 2.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations, HIPAA-compliant applications.' },
  { name: 'SaaS & Technology', description: 'Product development and modernization for SaaS companies at any stage.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, and real estate marketplaces.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse and supply chain platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE7: Record<string, CityAgencyData> = {

  bratislava: {
    slug: 'software-development-agency-bratislava',
    cityName: 'Bratislava',
    countryName: 'Slovakia',
    flag: '🇸🇰',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Bratislava | Call IT Dev',
      fr: 'Agence Développement Informatique Bratislava | Call IT Dev',
      de: 'Softwareentwicklung Agentur Bratislava | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Bratislava. Custom SaaS, web, mobile, AI. CET timezone, from €20/hr. 60% savings vs local rates.',
      fr: 'Agence de développement informatique pour Bratislava. SaaS, web, mobile, IA. Fuseau CET, dès 20 €/h.',
      de: 'Softwareentwicklungsagentur für Bratislava. SaaS, Web, Mobile, KI. CET-Zeitzone, ab 20 €/h.',
    },
    keywords: ['software development agency Bratislava', 'IT outsourcing Slovakia', 'nearshore development Bratislava', 'web development Slovakia'],
    heroTitle: {
      en: 'Software Development Agency for Bratislava',
      fr: 'Agence de Développement Informatique pour Bratislava',
      de: 'Softwareentwicklung Agentur für Bratislava',
    },
    heroSubtitle: {
      en: 'CET-aligned engineers at nearshore rates. 60% savings vs Bratislava agencies. Your extended dev team.',
      fr: 'Ingénieurs en fuseau CET à tarifs nearshore. 60% d\'économies vs agences de Bratislava.',
      de: 'CET-Ingenieure zu Nearshore-Preisen. 60% Ersparnis vs Bratislava-Agenturen.',
    },
    whyTitle: {
      en: 'Why Bratislava Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Bratislava Choisissent Call IT Dev',
      de: 'Warum Bratislavaer Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '⏰', title: 'Same CET Timezone', description: 'Real-time collaboration with identical working hours.' },
      { icon: '💰', title: '60% Cost Savings', description: 'Senior devs at €20-35/hr vs €50-80/hr locally.' },
      { icon: '🎓', title: 'Top Engineering Talent', description: '200+ engineers with expertise in modern tech stacks.' },
      { icon: '🌍', title: 'Multilingual Teams', description: 'English, French, German-speaking engineers available.' },
      { icon: '🛡️', title: 'EU GDPR Compliant', description: 'Enterprise security standards, full GDPR compliance.' },
      { icon: '🚀', title: '2-Week Team Setup', description: 'Dedicated team operational in just 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why choose nearshore over Bratislava agencies?', answer: 'Same CET timezone, 60% lower rates, same quality. Our engineers integrate seamlessly into your workflow.' },
      { question: 'What technologies do you work with?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP, Docker, Kubernetes, and more.' },
      { question: 'Do you comply with EU data protection?', answer: 'Yes. Enterprise security standards, fully GDPR compliant with signed DPAs.' },
      { question: 'How quickly can you start?', answer: 'Team onboarding in 2 weeks. CVs and interviews within 5 business days.' },
      { question: 'What engagement models do you offer?', answer: 'Time & materials, fixed-price, and dedicated team models. Flexible monthly contracts.' },
      { question: 'Can you scale the team?', answer: 'Start with 2 developers, scale to 50+. No long-term commitment required.' },
    ],
    localRate: '€50-80',
    ourRate: '€20-35',
    savings: '60%+',
  },

  budapest: {
    slug: 'software-development-agency-budapest',
    cityName: 'Budapest',
    countryName: 'Hungary',
    flag: '🇭🇺',
    timezone: 'CET (GMT+1)',
    currency: 'HUF/EUR',
    title: {
      en: 'Software Development Agency Budapest | Call IT Dev',
      fr: 'Agence Développement Informatique Budapest | Call IT Dev',
      de: 'Softwareentwicklung Agentur Budapest | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Budapest. Custom SaaS, web, mobile, AI. CET timezone, from €20/hr. 55% savings vs local Hungarian rates.',
      fr: 'Agence de développement informatique pour Budapest. SaaS, web, mobile, IA. Dès 20 €/h. 55% d\'économies.',
      de: 'Softwareentwicklungsagentur für Budapest. SaaS, Web, Mobile, KI. Ab 20 €/h. 55% Ersparnis.',
    },
    keywords: ['software development agency Budapest', 'IT outsourcing Hungary', 'nearshore development Budapest', 'web development Hungary'],
    heroTitle: {
      en: 'Software Development Agency for Budapest',
      fr: 'Agence de Développement Informatique pour Budapest',
      de: 'Softwareentwicklung Agentur für Budapest',
    },
    heroSubtitle: {
      en: 'CET-aligned engineers at nearshore rates. 55% savings vs Budapest agencies. Operational in 2 weeks.',
      fr: 'Ingénieurs CET à tarifs nearshore. 55% d\'économies vs agences budapestoises.',
      de: 'CET-Ingenieure zu Nearshore-Preisen. 55% Ersparnis vs Budapester Agenturen.',
    },
    whyTitle: {
      en: 'Why Budapest Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Budapest Choisissent Call IT Dev',
      de: 'Warum Budapester Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '⏰', title: 'CET Timezone Match', description: 'Identical working hours for seamless collaboration.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €20-35/hr vs €45-75/hr in Budapest.' },
      { icon: '🎓', title: '200+ Engineers', description: 'Full-stack, AI/ML, DevOps, and mobile specialists.' },
      { icon: '🌍', title: 'EU Standards', description: 'ISO 27001, GDPR compliant, EU data protection.' },
      { icon: '✈️', title: 'Easy Access', description: 'Direct flights and same-day collaboration capabilities.' },
      { icon: '🚀', title: 'Fast Onboarding', description: 'Team operational in 2 weeks with full integration.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does nearshore compare to Budapest agencies?', answer: 'Same CET timezone, 55% lower rates. Our engineers work as an extension of your Budapest team.' },
      { question: 'What stack do you support?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP, and modern DevOps tooling.' },
      { question: 'Are you GDPR compliant?', answer: 'Yes. Enterprise security standards, GDPR compliant. Signed DPAs and enterprise security.' },
      { question: 'Minimum team size?', answer: 'Start with 2 developers. Scale to 50+ as needed.' },
      { question: 'How do you handle communication?', answer: 'Daily standups, Slack/Teams, Jira integration, bi-weekly demos. Same timezone = real-time collaboration.' },
      { question: 'What about IP ownership?', answer: 'All IP belongs to you. NDAs and IP assignment agreements signed from day one.' },
    ],
    localRate: '€45-75',
    ourRate: '€20-35',
    savings: '55%+',
  },

  zagreb: {
    slug: 'software-development-agency-zagreb',
    cityName: 'Zagreb',
    countryName: 'Croatia',
    flag: '🇭🇷',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Zagreb | Call IT Dev',
      fr: 'Agence Développement Informatique Zagreb | Call IT Dev',
      de: 'Softwareentwicklung Agentur Zagreb | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Zagreb. Custom SaaS, web, mobile, AI. CET timezone, from €20/hr. 55% savings vs Croatian rates.',
      fr: 'Agence développement informatique pour Zagreb. SaaS, web, mobile, IA. Dès 20 €/h.',
      de: 'Softwareentwicklungsagentur für Zagreb. SaaS, Web, Mobile, KI. Ab 20 €/h.',
    },
    keywords: ['software development agency Zagreb', 'IT outsourcing Croatia', 'nearshore development Zagreb', 'web development Croatia'],
    heroTitle: {
      en: 'Software Development Agency for Zagreb',
      fr: 'Agence de Développement Informatique pour Zagreb',
      de: 'Softwareentwicklung Agentur für Zagreb',
    },
    heroSubtitle: {
      en: 'CET-aligned engineers, 55% savings vs Zagreb agencies. EU-compliant nearshore development.',
      fr: 'Ingénieurs CET, 55% d\'économies vs agences zagreboises. Développement nearshore conforme UE.',
      de: 'CET-Ingenieure, 55% Ersparnis vs Zagreber Agenturen. EU-konformes Nearshore.',
    },
    whyTitle: {
      en: 'Why Zagreb Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Zagreb Choisissent Call IT Dev',
      de: 'Warum Zagreber Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '⏰', title: 'CET Timezone', description: 'Same working hours as Zagreb for real-time collaboration.' },
      { icon: '💰', title: '55% Savings', description: 'Senior developers at €20-35/hr vs €45-70/hr locally.' },
      { icon: '🇪🇺', title: 'EU Compliant', description: 'ISO 27001, GDPR, enterprise security standards.' },
      { icon: '🎓', title: 'Engineering Excellence', description: '200+ engineers across all modern tech stacks.' },
      { icon: '🌍', title: 'Multilingual', description: 'English, French, Arabic, German-speaking teams.' },
      { icon: '🚀', title: 'Quick Start', description: '2-week onboarding, immediate productivity.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why nearshore instead of local Zagreb developers?', answer: 'Same quality and timezone at 55% lower cost. Our teams integrate seamlessly with Croatian companies.' },
      { question: 'Technologies used?', answer: 'React, TypeScript, Node.js, Python, Java, cloud-native on AWS/Azure/GCP.' },
      { question: 'GDPR compliance?', answer: 'Fully compliant. ISO 27001, signed DPAs, EU data protection standards.' },
      { question: 'How fast to start?', answer: '2-week team setup. CVs within 5 business days.' },
      { question: 'Engagement models?', answer: 'T&M, fixed-price, dedicated teams. Monthly flexibility.' },
      { question: 'Team scaling?', answer: 'Start with 2, scale to 50+. No lock-in contracts.' },
    ],
    localRate: '€45-70',
    ourRate: '€20-35',
    savings: '55%+',
  },

  riga: {
    slug: 'software-development-agency-riga',
    cityName: 'Riga',
    countryName: 'Latvia',
    flag: '🇱🇻',
    timezone: 'EET (GMT+2)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Riga | Call IT Dev',
      fr: 'Agence Développement Informatique Riga | Call IT Dev',
      de: 'Softwareentwicklung Agentur Riga | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Riga, Latvia. Custom SaaS, web, mobile, AI. From €20/hr. 55% savings vs Baltic rates.',
      fr: 'Agence développement informatique pour Riga. SaaS, web, mobile, IA. Dès 20 €/h.',
      de: 'Softwareentwicklungsagentur für Riga. SaaS, Web, Mobile, KI. Ab 20 €/h.',
    },
    keywords: ['software development agency Riga', 'IT outsourcing Latvia', 'nearshore development Riga', 'web development Latvia'],
    heroTitle: {
      en: 'Software Development Agency for Riga',
      fr: 'Agence de Développement Informatique pour Riga',
      de: 'Softwareentwicklung Agentur für Riga',
    },
    heroSubtitle: {
      en: '1-hour time difference, 55% savings vs Riga agencies. EU-compliant nearshore partner.',
      fr: '1h de décalage, 55% d\'économies vs agences de Riga. Partenaire nearshore conforme UE.',
      de: '1h Zeitunterschied, 55% Ersparnis vs Rigaer Agenturen. EU-konformer Nearshore-Partner.',
    },
    whyTitle: {
      en: 'Why Riga Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Riga Choisissent Call IT Dev',
      de: 'Warum Rigaer Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '⏰', title: '1h Time Difference', description: 'Near-identical working hours for real-time collaboration.' },
      { icon: '💰', title: '55% Cost Savings', description: 'Senior devs at €20-35/hr vs €45-70/hr in Riga.' },
      { icon: '🇪🇺', title: 'EU Standards', description: 'ISO 27001, GDPR, full EU compliance.' },
      { icon: '🎓', title: 'Expert Engineers', description: '200+ engineers across modern stacks.' },
      { icon: '🌍', title: 'English Fluent', description: 'C1/C2 English-speaking development teams.' },
      { icon: '🚀', title: 'Rapid Setup', description: 'Dedicated team in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does the timezone work with Riga?', answer: 'Morocco is CET (GMT+1), Riga is EET (GMT+2). Just 1 hour difference — full overlap for collaboration.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, .NET, AWS, Azure, GCP.' },
      { question: 'EU compliance?', answer: 'ISO 27001, GDPR compliant. Signed DPAs, enterprise security.' },
      { question: 'Minimum engagement?', answer: 'Start with 2 developers, scale as needed. Monthly flexibility.' },
      { question: 'IP ownership?', answer: 'All IP belongs to you. NDAs and IP assignment from day one.' },
      { question: 'How to start?', answer: 'CVs in 5 days, team onboarding in 2 weeks, first sprint week 3.' },
    ],
    localRate: '€45-70',
    ourRate: '€20-35',
    savings: '55%+',
  },

  vilnius: {
    slug: 'software-development-agency-vilnius',
    cityName: 'Vilnius',
    countryName: 'Lithuania',
    flag: '🇱🇹',
    timezone: 'EET (GMT+2)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Vilnius | Call IT Dev',
      fr: 'Agence Développement Informatique Vilnius | Call IT Dev',
      de: 'Softwareentwicklung Agentur Vilnius | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Vilnius, Lithuania. Custom SaaS, web, mobile, AI. From €20/hr. 55% savings vs Lithuanian rates.',
      fr: 'Agence développement informatique pour Vilnius. SaaS, web, mobile, IA. Dès 20 €/h.',
      de: 'Softwareentwicklungsagentur für Vilnius. SaaS, Web, Mobile, KI. Ab 20 €/h.',
    },
    keywords: ['software development agency Vilnius', 'IT outsourcing Lithuania', 'nearshore development Vilnius', 'web development Lithuania'],
    heroTitle: {
      en: 'Software Development Agency for Vilnius',
      fr: 'Agence de Développement Informatique pour Vilnius',
      de: 'Softwareentwicklung Agentur für Vilnius',
    },
    heroSubtitle: {
      en: '1-hour time difference, 55% savings vs Vilnius agencies. EU-compliant nearshore development.',
      fr: '1h de décalage, 55% d\'économies vs agences de Vilnius. Nearshore conforme UE.',
      de: '1h Zeitunterschied, 55% Ersparnis vs Vilniuser Agenturen.',
    },
    whyTitle: {
      en: 'Why Vilnius Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Vilnius Choisissent Call IT Dev',
      de: 'Warum Vilniuser Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '⏰', title: '1h Time Difference', description: 'Near-identical working hours with Lithuanian companies.' },
      { icon: '💰', title: '55% Savings', description: 'Senior devs at €20-35/hr vs €45-70/hr in Vilnius.' },
      { icon: '🇪🇺', title: 'EU Compliant', description: 'ISO 27001, GDPR, enterprise security.' },
      { icon: '🎓', title: '200+ Engineers', description: 'Full-stack, AI, DevOps specialists.' },
      { icon: '🌍', title: 'English Fluent', description: 'C1/C2 English teams, multilingual support.' },
      { icon: '🚀', title: '2-Week Setup', description: 'Fast onboarding, immediate productivity.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Timezone compatibility with Vilnius?', answer: 'Just 1 hour difference (CET vs EET). Full working day overlap.' },
      { question: 'Tech stack?', answer: 'React, TypeScript, Node.js, Python, Java, cloud-native on AWS/Azure/GCP.' },
      { question: 'GDPR?', answer: 'Fully compliant. ISO 27001, signed DPAs, EU standards.' },
      { question: 'Starting size?', answer: '2 developers minimum, scale to 50+.' },
      { question: 'Contract flexibility?', answer: 'Monthly contracts, no long-term lock-in.' },
      { question: 'Communication?', answer: 'Daily standups, Slack/Teams, Jira, bi-weekly demos.' },
    ],
    localRate: '€45-70',
    ourRate: '€20-35',
    savings: '55%+',
  },

  capeTown: {
    slug: 'software-development-agency-cape-town',
    cityName: 'Cape Town',
    countryName: 'South Africa',
    flag: '🇿🇦',
    timezone: 'SAST (GMT+2)',
    currency: 'ZAR',
    title: {
      en: 'Software Development Agency Cape Town | Call IT Dev',
      fr: 'Agence Développement Informatique Le Cap | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Cape Town. Custom SaaS, web, mobile, AI. From $20/hr. 65% savings vs Cape Town rates.',
      fr: 'Agence développement informatique pour Le Cap. SaaS, web, mobile, IA. Dès 20 $/h.',
    },
    keywords: ['software development agency Cape Town', 'IT outsourcing South Africa', 'nearshore development Cape Town', 'web development South Africa'],
    heroTitle: {
      en: 'Software Development Agency for Cape Town',
      fr: 'Agence de Développement Informatique pour Le Cap',
    },
    heroSubtitle: {
      en: '1-hour time difference, 65% savings vs Cape Town agencies. Your nearshore development partner.',
      fr: '1h de décalage, 65% d\'économies vs agences du Cap.',
    },
    whyTitle: {
      en: 'Why Cape Town Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises du Cap Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '⏰', title: '1h Time Difference', description: 'Near-identical working hours with South Africa.' },
      { icon: '💰', title: '65% Savings', description: 'Senior devs at $20-35/hr vs $50-80/hr in Cape Town.' },
      { icon: '🌍', title: 'English Fluent', description: 'Native English-speaking engineering teams.' },
      { icon: '🎓', title: 'Top Talent', description: '200+ engineers across modern tech stacks.' },
      { icon: '🛡️', title: 'POPIA Compliant', description: 'ISO 27001, GDPR, and South African POPIA compliance.' },
      { icon: '🚀', title: 'Quick Setup', description: 'Dedicated team operational in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Timezone with Cape Town?', answer: 'Just 1 hour difference (CET vs SAST). Full day overlap.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
      { question: 'Data protection?', answer: 'ISO 27001, GDPR, POPIA compliant. Signed DPAs.' },
      { question: 'Minimum team?', answer: 'Start with 2 developers, scale to 50+.' },
      { question: 'How to start?', answer: 'CVs in 5 days, onboarding in 2 weeks.' },
      { question: 'IP ownership?', answer: 'All IP belongs to you from day one.' },
    ],
    localRate: '$50-80',
    ourRate: '$20-35',
    savings: '65%+',
  },

  kampala: {
    slug: 'software-development-agency-kampala',
    cityName: 'Kampala',
    countryName: 'Uganda',
    flag: '🇺🇬',
    timezone: 'EAT (GMT+3)',
    currency: 'UGX',
    title: {
      en: 'Software Development Agency Kampala | Call IT Dev',
      fr: 'Agence Développement Informatique Kampala | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Kampala, Uganda. Custom SaaS, web, mobile, AI. From $18/hr. 70% savings vs Western rates.',
      fr: 'Agence développement informatique pour Kampala. SaaS, web, mobile, IA. Dès 18 $/h.',
    },
    keywords: ['software development agency Kampala', 'IT outsourcing Uganda', 'software development East Africa', 'web development Kampala'],
    heroTitle: {
      en: 'Software Development Agency for Kampala',
      fr: 'Agence de Développement Informatique pour Kampala',
    },
    heroSubtitle: {
      en: '2-hour time difference from CET, 70% savings. Nearshore development for East African businesses.',
      fr: '2h de décalage vs CET, 70% d\'économies. Développement nearshore pour l\'Afrique de l\'Est.',
    },
    whyTitle: {
      en: 'Why Kampala Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Kampala Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '⏰', title: '2h Time Difference', description: 'Close timezone alignment with East Africa.' },
      { icon: '💰', title: '70% Savings', description: 'Enterprise-grade development at competitive rates.' },
      { icon: '🌍', title: 'English & French', description: 'Bilingual teams for East African markets.' },
      { icon: '🎓', title: 'Expert Engineers', description: '200+ full-stack, mobile, and AI specialists.' },
      { icon: '🛡️', title: 'Security First', description: 'ISO 27001, GDPR compliance, enterprise security.' },
      { icon: '🚀', title: 'Fast Delivery', description: '2-week setup, agile sprints, continuous delivery.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why nearshore for Kampala?', answer: 'Access 200+ engineers at competitive rates with 2h timezone overlap. Enterprise quality at scale.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, AWS, Azure, mobile (React Native, Flutter).' },
      { question: 'Data security?', answer: 'Enterprise security standards, GDPR compliant, signed NDAs.' },
      { question: 'Minimum engagement?', answer: '2 developers minimum, scale as your needs grow.' },
      { question: 'Communication?', answer: 'English-speaking teams, daily standups, Slack/Teams integration.' },
      { question: 'How to start?', answer: 'Free consultation, CVs in 5 days, team ready in 2 weeks.' },
    ],
    localRate: '$40-60',
    ourRate: '$18-30',
    savings: '70%+',
  },

  maputo: {
    slug: 'software-development-agency-maputo',
    cityName: 'Maputo',
    countryName: 'Mozambique',
    flag: '🇲🇿',
    timezone: 'CAT (GMT+2)',
    currency: 'MZN',
    title: {
      en: 'Software Development Agency Maputo | Call IT Dev',
      fr: 'Agence Développement Informatique Maputo | Call IT Dev',
      pt: 'Agência Desenvolvimento Software Maputo | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Maputo, Mozambique. Custom SaaS, web, mobile, AI. From $18/hr. Portuguese-speaking teams.',
      fr: 'Agence développement informatique pour Maputo. SaaS, web, mobile, IA. Équipes lusophones.',
      pt: 'Agência de desenvolvimento de software para Maputo. SaaS, web, mobile, IA. Equipes lusófonas.',
    },
    keywords: ['software development agency Maputo', 'IT outsourcing Mozambique', 'desenvolvimento software Maputo', 'nearshore Mozambique'],
    heroTitle: {
      en: 'Software Development Agency for Maputo',
      fr: 'Agence de Développement Informatique pour Maputo',
      pt: 'Agência de Desenvolvimento de Software para Maputo',
    },
    heroSubtitle: {
      en: 'Portuguese-speaking engineers, 1h time difference, 70% savings. Your Lusophone nearshore partner.',
      fr: 'Ingénieurs lusophones, 1h de décalage, 70% d\'économies.',
      pt: 'Engenheiros lusófonos, 1h de diferença, 70% de economia.',
    },
    whyTitle: {
      en: 'Why Maputo Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Maputo Choisissent Call IT Dev',
      pt: 'Por Que Empresas de Maputo Escolhem Call IT Dev',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Portuguese Teams', description: 'Native Portuguese-speaking engineers for Lusophone markets.' },
      { icon: '⏰', title: '1h Time Difference', description: 'Close timezone alignment with Mozambique.' },
      { icon: '💰', title: '70% Savings', description: 'Enterprise development at competitive nearshore rates.' },
      { icon: '🎓', title: 'Expert Engineers', description: '200+ full-stack and AI specialists.' },
      { icon: '🛡️', title: 'Data Security', description: 'ISO 27001, GDPR, enterprise-grade security.' },
      { icon: '🚀', title: 'Quick Setup', description: 'Team ready in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have Portuguese-speaking teams?', answer: 'Yes. Native Portuguese speakers available for Mozambique and Lusophone markets.' },
      { question: 'Timezone?', answer: '1 hour difference (CET vs CAT). Full day overlap.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, AWS, Azure, GCP, mobile development.' },
      { question: 'Data protection?', answer: 'ISO 27001, GDPR compliant, enterprise security standards.' },
      { question: 'How to start?', answer: 'Free consultation, team setup in 2 weeks.' },
      { question: 'Minimum team?', answer: '2 developers, scale to 50+.' },
    ],
    localRate: '$35-55',
    ourRate: '$18-30',
    savings: '70%+',
  },

  algiers: {
    slug: 'software-development-agency-algiers',
    cityName: 'Algiers',
    countryName: 'Algeria',
    flag: '🇩🇿',
    timezone: 'CET (GMT+1)',
    currency: 'DZD',
    title: {
      en: 'Software Development Agency Algiers | Call IT Dev',
      fr: 'Agence Développement Informatique Alger | Call IT Dev',
      ar: 'وكالة تطوير برمجيات الجزائر | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Algiers, Algeria. Custom SaaS, web, mobile, AI. CET timezone, from $20/hr. French & Arabic speaking teams.',
      fr: 'Agence développement informatique pour Alger. SaaS, web, mobile, IA. Fuseau CET, dès 20 $/h. Équipes francophone et arabophone.',
      ar: 'وكالة تطوير برمجيات للجزائر. SaaS، ويب، موبايل، ذكاء اصطناعي. منطقة CET، من 20 $/ساعة.',
    },
    keywords: ['software development agency Algiers', 'agence développement informatique Alger', 'IT outsourcing Algeria', 'nearshore Algérie'],
    heroTitle: {
      en: 'Software Development Agency for Algiers',
      fr: 'Agence de Développement Informatique pour Alger',
      ar: 'وكالة تطوير برمجيات للجزائر',
    },
    heroSubtitle: {
      en: 'French & Arabic speaking engineers, same CET timezone, 65% savings. Your Maghreb nearshore partner.',
      fr: 'Ingénieurs francophones et arabophones, même fuseau CET, 65% d\'économies. Votre partenaire nearshore au Maghreb.',
      ar: 'مهندسون فرنكوفونيون وعرب، نفس منطقة CET، توفير 65%. شريكك القريب في المغرب العربي.',
    },
    whyTitle: {
      en: 'Why Algiers Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises d\'Alger Choisissent Call IT Dev',
      ar: 'لماذا تختار شركات الجزائر Call IT Dev',
    },
    whyReasons: [
      { icon: '🗣️', title: 'French & Arabic', description: 'Bilingual teams perfectly suited for Algerian businesses.' },
      { icon: '⏰', title: 'Same CET Timezone', description: 'Identical working hours for seamless collaboration.' },
      { icon: '💰', title: '65% Savings', description: 'Enterprise development at competitive Maghreb rates.' },
      { icon: '🎓', title: 'Engineering Excellence', description: '200+ engineers across all modern stacks.' },
      { icon: '🌍', title: 'Maghreb Expertise', description: 'Deep understanding of North African business culture.' },
      { icon: '🚀', title: 'Fast Delivery', description: 'Team operational in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Avez-vous des équipes francophones?', answer: 'Oui. Tous nos ingénieurs parlent français nativement, ainsi qu\'arabe et anglais.' },
      { question: 'Timezone?', answer: 'Same CET timezone as Algeria. Identical working hours.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, AWS, Azure, GCP, AI/ML.' },
      { question: 'Data protection?', answer: 'ISO 27001, GDPR compliant, enterprise security.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Scalability?', answer: 'Start with 2 devs, scale to 50+. Monthly contracts.' },
    ],
    localRate: '$40-65',
    ourRate: '$20-35',
    savings: '65%+',
  },

  colombo: {
    slug: 'software-development-agency-colombo',
    cityName: 'Colombo',
    countryName: 'Sri Lanka',
    flag: '🇱🇰',
    timezone: 'IST (GMT+5:30)',
    currency: 'LKR',
    title: {
      en: 'Software Development Agency Colombo | Call IT Dev',
      fr: 'Agence Développement Informatique Colombo | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Colombo, Sri Lanka. Custom SaaS, web, mobile, AI. From $18/hr. English-speaking teams, 70% savings.',
      fr: 'Agence développement informatique pour Colombo. SaaS, web, mobile, IA. Dès 18 $/h. 70% d\'économies.',
    },
    keywords: ['software development agency Colombo', 'IT outsourcing Sri Lanka', 'nearshore development Colombo', 'web development Sri Lanka'],
    heroTitle: {
      en: 'Software Development Agency for Colombo',
      fr: 'Agence de Développement Informatique pour Colombo',
    },
    heroSubtitle: {
      en: 'English-speaking engineers, 70% savings vs Western rates. Your South Asian development partner.',
      fr: 'Ingénieurs anglophones, 70% d\'économies. Votre partenaire de développement en Asie du Sud.',
    },
    whyTitle: {
      en: 'Why Colombo Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Colombo Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'English Fluent', description: 'Sri Lankan business culture aligned with English-first communication.' },
      { icon: '💰', title: '70% Savings', description: 'Enterprise development at highly competitive rates.' },
      { icon: '🎓', title: 'Expert Engineers', description: '200+ engineers across full-stack, AI, DevOps.' },
      { icon: '⏰', title: 'Flexible Hours', description: 'Overlapping hours for UK, EU, and Asian markets.' },
      { icon: '🛡️', title: 'Security First', description: 'ISO 27001, GDPR compliance.' },
      { icon: '🚀', title: 'Fast Setup', description: 'Team ready in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why nearshore for Colombo?', answer: 'Access 200+ engineers with 70% savings. Enterprise quality, flexible engagement models.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
      { question: 'Data protection?', answer: 'ISO 27001, GDPR compliant, signed NDAs and DPAs.' },
      { question: 'Communication?', answer: 'English-speaking teams, daily standups, Slack/Teams, Jira.' },
      { question: 'How to start?', answer: 'Free consultation, CVs in 5 days, team in 2 weeks.' },
      { question: 'Minimum team?', answer: '2 developers, scale to 50+.' },
    ],
    localRate: '$35-55',
    ourRate: '$18-30',
    savings: '70%+',
  },
};
